import React, { useState } from 'react'
import {ArrowLeft, Star, Calendar, Users, Heart} from 'lucide-react'
import { Link } from 'react-router-dom'
import { RatingSystem } from '../components/RatingSystem'
import { SocialShare } from '../components/SocialShare'
import { CommentSection } from '../components/CommentSection'
import { UserAuth } from '../components/UserAuth'
import { useAuth } from '../context/AuthContext'

export function MorphleCartoon() {
  const { user, toggleFavorite, isFavorite } = useAuth()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const contentId = 'morphle-cartoon'
  const favorite = isFavorite(contentId)

  const handleFavoriteClick = () => {
    if (!user) {
      setShowAuthModal(true)
      return
    }
    toggleFavorite(contentId)
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-pink-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-red-500 hover:text-red-600 mb-8 font-semibold">
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </Link>

        <article className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <img 
            src="https://static.lumi.new/d8/d859fdb4778979e3124cacdf655830a3.png" 
            alt="Morphle Cartoon"
            className="w-full h-96 object-cover"
          />
          
          <div className="p-8 sm:p-12">
            <div className="flex items-center gap-4 mb-6 flex-wrap">
              <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                FOR KIDS
              </span>
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>2019-Present</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Users className="w-4 h-4" />
                <span>Ages 3-7</span>
              </div>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-6">
              Watch Cartoon Morphle
            </h1>

            <div className="prose prose-lg max-w-none space-y-6 text-gray-700">
              <p className="text-xl leading-relaxed">
                <strong>Morphle Cartoon</strong> is the perfect magical adventure for young children! 
                This modern animated series features Morphle, an adorable red shape-shifting pet who 
                can transform into anything his best friend Mila imagines.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Meet Morphle</h2>
              <p>
                Morphle is not your ordinary pet—he's a magical creature who can morph into vehicles, 
                animals, objects, and anything else Mila needs for their adventures. Each episode of 
                <strong> Cartoon Morphle</strong> features creative transformations that spark imagination 
                and teach valuable lessons about friendship, problem-solving, and creativity.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Why Kids Love It</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Colorful, eye-catching animation perfect for young viewers</li>
                <li>Endless shape-shifting possibilities keep each episode fresh</li>
                <li>Positive messages about friendship and helping others</li>
                <li>Simple storylines easy for preschoolers to follow</li>
                <li>Educational elements woven into entertaining adventures</li>
              </ul>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Popular Episodes</h2>
              <p>
                The <strong>Morphle Cartoon</strong> series features hundreds of episodes, each showcasing 
                different transformations and adventures. From turning into a fire truck to rescue friends, 
                to becoming a dinosaur for prehistoric fun, Morphle's magical abilities know no bounds.
              </p>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Educational Value</h2>
              <p>
                Beyond entertainment, <strong>Cartoon Morphle</strong> teaches children about:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Colors, shapes, and object recognition</li>
                <li>Problem-solving and creative thinking</li>
                <li>Emotional intelligence and empathy</li>
                <li>The importance of friendship and teamwork</li>
                <li>Basic safety lessons in fun, approachable ways</li>
              </ul>

              <h2 className="text-3xl font-bold text-gray-800 mt-8 mb-4">Our Verdict</h2>
              <p>
                <strong>Morphle Cartoon</strong> is an excellent choice for parents seeking quality 
                entertainment for young children. The combination of magical transformations, vibrant 
                animation, and positive messaging makes it a standout in modern children's programming. 
                It's engaging enough to keep kids entertained while subtle enough in its educational 
                content that learning feels like pure fun.
              </p>

              <div className="bg-gradient-to-r from-red-100 to-pink-100 rounded-2xl p-6 mt-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Quick Facts</h3>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>First Aired:</strong> 2019</li>
                  <li><strong>Episode Length:</strong> 3-5 minutes</li>
                  <li><strong>Genre:</strong> Children's Animation, Educational</li>
                  <li><strong>Best For:</strong> Preschoolers ages 3-7</li>
                  <li><strong>Available:</strong> YouTube, streaming platforms</li>
                </ul>
              </div>
            </div>

            {/* Social Share and Favorite */}
            <div className="mt-8 flex justify-center gap-4 flex-wrap">
              <button
                onClick={handleFavoriteClick}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all transform hover:scale-105 ${
                  favorite
                    ? 'bg-pink-500 text-white hover:bg-pink-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <Heart className={`w-5 h-5 ${favorite ? 'fill-white' : ''}`} />
                {favorite ? 'Favorited' : 'Add to Favorites'}
              </button>
              <SocialShare title="Morphle Cartoon" />
            </div>
          </div>
        </article>

        {/* Rating System */}
        <div className="mt-8">
          <RatingSystem contentTitle="Morphle Cartoon" />
        </div>

        {/* Comment Section */}
        <div className="mt-8">
          <CommentSection 
            contentId={contentId}
            onAuthRequired={() => setShowAuthModal(true)}
          />
        </div>
      </div>

      {/* Auth Modal */}
      <UserAuth isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  )
}
